import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Button, Card, Popper, Stack} from '@mui/material';
import {useEffectFn} from '@ngneat/effects-hooks';
import {useObservable} from '@ngneat/react-rxjs';

import ModalComponent from '@components/modal/Modal.component';
import TableFiltersComponent from '@components/table/filters/TableFilters.component';
import SearchComponent from '@components/inputs/search/Search.component';
import {emitOnce} from '@ngneat/elf';
import NewTableComponent from '@components/table/NewTable.component';
import COLUMN_TYPES from '@utils/table/columnTypes.table';
import {GridValueGetterParams} from '@mui/x-data-grid-pro';
import {executionLineQuery} from '@store/invoices/executionLines';
import {executionsQuery} from '@store/operations/executions';
import {AddBatchProvenance} from '@screens/admin/operations/executions/addExecutions/AddBatchExecutions.index';
import {ClientsContract, clientsContractsQuery, clientsContractsService} from '@store/clients/contracts';
import {searchClientsContractsEffect} from '@store/clients/contracts/clients-contracts.effects';
import ManageContractDrawer from '@screens/admin/clients/details/contracts/manageContract/ManageContract.drawer';
import I18n from '@utils/i18n.utils';
import ContractServicesModal
  from '@screens/admin/clients/details/contractedServices/manageService/steps/ContractServices.modal';
import CheckboxComponent from '@components/inputs/checkbox/Checkbox.component';
import ClientsServicesTable from '@screens/admin/clients/services/ClientsServices.table';

interface SelectClientContractsModalProps {
  handleClose: () => void;
  handleValidate: (selectedClientServices: ClientsContract[]) => void;
  from?: AddBatchProvenance;
}

const SelectClientContractsModal = (props: SelectClientContractsModalProps) => {
  const {handleClose, handleValidate, from} = props;

  const {t} = useTranslation();

  const [popoverAnchorEl, setPopoverAnchorEl] = useState<HTMLElement | null>(null);
  const [popoverContract, setPopoverContract] = useState<ClientsContract | null>(null);

  const [showAddContract, setShowAddContract] = useState(false);

  const searchClientsContracts = useEffectFn(searchClientsContractsEffect);

  const [{clientsContracts, loading}] = useObservable(clientsContractsQuery.clientsContracts$);
  const [selectedClientsContracts, setSelectedClientsContracts] = useState<ClientsContract[]>([]);

  const [filters] = useObservable(clientsContractsQuery.filters$);
  const [sortModel] = useObservable(clientsContractsQuery.sort$);
  const [tablePagination] = useObservable(clientsContractsQuery.pagination$);

  const [execLineFilters] = useObservable(executionLineQuery.filters$);
  const [execFilters] = useObservable(executionsQuery.filters$);

  const [showContractServices, setShowContractServices] = useState<ClientsContract | undefined>(undefined);

  useEffect(() => {
    if (from === AddBatchProvenance.EXEC_LINE && execLineFilters.tableFilters.length > 0) {
      clientsContractsService.setFilters('tableFilters')(execLineFilters.tableFilters);
    } else if (from === AddBatchProvenance.EXEC && execFilters.tableFilters.length > 0) {
      clientsContractsService.setFilters('tableFilters')(execFilters.tableFilters);
    }
  }, [execFilters, execLineFilters, from]);

  useEffect(() => {
    searchClientsContracts({filters, tablePagination, sort: sortModel});
  }, [filters, searchClientsContracts, sortModel, tablePagination]);

  if (showAddContract) {
    return (
      <ManageContractDrawer
        handleClose={() => setShowAddContract(false)}
        handleCreated={(newContract) => {
          if (newContract) clientsContractsService.setSelectedClientsContracts(selectedClientsContracts.map((cs) => cs.id).concat(newContract.id));
        }}
        isCreation/>
    );
  }

  const popoverOpen = Boolean(popoverAnchorEl);

  return (
    <ModalComponent
      aria-label="small-padding"
      handleClose={handleClose}
      maxWidth={false}
      title={t('operations.batchExecutions.selectClientsContracts')}
      content={(
        <Stack pt="20px" height="85vh" width="100%" spacing={2} overflow="hidden">
          {!!showContractServices && (
            <ContractServicesModal
              contract={showContractServices}
              contractId={showContractServices.id}
              handleClose={() => setShowContractServices(undefined)}/>
          )}
          <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
            <Button variant="outlined" color="primary" onClick={() => setShowAddContract(true)}>
              {t('operations.batchExecutions.addContractButton')}
            </Button>
            <Stack direction="row" spacing={2}>
              <CheckboxComponent
                label={t('clients.details.contractedServices.manage.selectContract.table.filterExpiredContracts')}
                value={filters.filterExpiredContracts}
                handleChange={clientsContractsService.setFilters('filterExpiredContracts')}
              />
              <TableFiltersComponent
                filters={filters}
                handleSave={clientsContractsService.setFilters('tableFilters')}
                handleReset={() => clientsContractsService.resetFilters()}/>
            </Stack>

          </Stack>
          <SearchComponent
            value={filters.search}
            handleChange={(value) => {
              emitOnce(() => {
                clientsContractsService.setFilters('search')(value);
                clientsContractsService.setPagination('page')(1);
              });
            }}/>
          <NewTableComponent
            loading={loading}
            columnThreshold={0}
            columns={[
              COLUMN_TYPES.TEXT_BOLD({
                field: 'contractReference',
                label: t('clients.details.contractedServices.manage.selectContract.table.reference'),
                width: 150
              }),
              COLUMN_TYPES.TEXT({
                field: 'clientOrganisationUnitName',
                label: t('clients.details.contractedServices.manage.selectContract.table.clientUnit'),
                minWidth: 350
              }),
              COLUMN_TYPES.TEXT_BOLD({
                field: 'contractName',
                label: t('clients.details.contractedServices.manage.selectContract.table.contractName'),
                fullWidth: true,
              }),
              COLUMN_TYPES.TEXT_BOLD({
                field: 'waste',
                label: t('clients.details.contractedServices.manage.selectContract.table.waste'),
                valueGetter: (params: GridValueGetterParams<any, ClientsContract>) => params.row.services.length ? params.row.services[0].wasteName : '-',
                fullWidth: true,
                sortable: false,
                width: 140
              }),
              COLUMN_TYPES.TEXT_BOLD({
                field: 'container',
                label: t('clients.details.contractedServices.manage.selectContract.table.container'),
                valueGetter: (params: GridValueGetterParams<any, ClientsContract>) => params.row.services.length ? params.row.services[0].containerName : '-',
                fullWidth: true,
                sortable: false,
                width: 140
              }),
              COLUMN_TYPES.TEXT_BOLD({
                field: 'pickupSupplierUnitName',
                label: t('clients.details.contractedServices.manage.selectContract.table.pickupSupplierUnitName'),
                valueGetter: (params: GridValueGetterParams<any, ClientsContract>) => params.row.pickupSupplierUnitName || '-',
                fullWidth: true,
                sortable: false,
                width: 140
              }),
              COLUMN_TYPES.TEXT({
                field: 'startDate',
                label: t('clients.details.contractedServices.manage.selectContract.table.startDate'),
                valueFormatter: (params) => params.value ? I18n.t('global.date', {date: new Date(params.value)}) : '',
                width: 140
              }),
              ...(!filters.filterExpiredContracts ? [COLUMN_TYPES.TEXT({
                field: 'endDate',
                label: t('clients.details.contractedServices.manage.selectContract.table.endDate'),
                valueFormatter: (params) => params.value ? I18n.t('global.date', {date: new Date(params.value)}) : '',
                width: 130
              })] : []),
              COLUMN_TYPES.TEXT_WITH_ACTION({
                field: 'servicesCount',
                actionLabel: (contract: ClientsContract) => contract.servicesCount,
                handleClick: (contract: ClientsContract) => setShowContractServices(contract),
                handleMouseOver: (contract: ClientsContract, event) => {
                  setPopoverAnchorEl(event.currentTarget);
                  setPopoverContract(contract);
                },
                handleMouseOut: (contract, event) => {
                  setPopoverAnchorEl(null);
                  setPopoverContract(null);
                },
                label: t('clients.details.contractedServices.manage.selectContract.table.servicesCount'),
                width: 100
              }),
            ]}
            getRowClassName={(params) => {
              if (params.row.endDate && new Date(params.row.endDate) < new Date()) {
                return 'grey-row';
              }
              return '';
            }}
            handlePageSizeChange={clientsContractsService.setPagination('limit')}
            pageSize={tablePagination.limit}
            handlePageChange={(page) => clientsContractsService.setPagination('page')(page + 1)}
            page={tablePagination.page - 1}
            handleSortModelChange={clientsContractsService.setSort}
            sortModel={[sortModel]}
            totalRows={clientsContractsQuery.totalRows}
            data={clientsContracts}
            handleRowSelected={(rowSelected) => {
              if (rowSelected.length === clientsContracts.length) {
                setSelectedClientsContracts((state) => state.concat(...clientsContracts));
                return;
              }
              if (rowSelected.length < selectedClientsContracts.length) {
                setSelectedClientsContracts((state) => state.filter((s) => rowSelected.includes(s.id)));
              } else if (rowSelected.length > selectedClientsContracts.length) {
                const newRowSelected = clientsContracts.find((cs) => cs.id === rowSelected[rowSelected.length - 1]);
                if (newRowSelected) setSelectedClientsContracts((state) => state.concat(newRowSelected));
              }
            }}
            rowsSelected={selectedClientsContracts.map((s) => s.id)}/>
          <Popper open={popoverOpen} anchorEl={popoverAnchorEl}>
            <Card>
              <ClientsServicesTable showRowsOnly={true} contractIds={popoverContract?.id ? [popoverContract?.id] : []}/>
            </Card>
          </Popper>
        </Stack>
      )}
      actions={(
        <Stack direction="row" alignItems="center" position="relative">
          <Button
            variant="contained"
            color="primary"
            disabled={!selectedClientsContracts.length}
            onClick={() => handleValidate(selectedClientsContracts)}>
            {t('global.validate')}
          </Button>
        </Stack>
      )}
    />
  );
};

export default SelectClientContractsModal;
